import React, { useEffect } from 'react';
import '../styles.css';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../../../store';
import { post } from '../../../../api/baseUrl';
import {
  setCurrentQuestionnaire,
  setDataAnnotationData,
} from '../../../../store/ai2';
import {
  AddSurveyResponsePayloadSidebar,
  toCaseTO,
  type AddSurveyResponsePayload,
  type CaseTO,
} from '../../../../lib';
import {
  CaseType,
  QuestionType,
  type GetAnnotationCasePayload,
} from '../types';

interface DataAnnotationContextProps {
  popUpOpen: boolean;
  currentCase: CaseTO | undefined;
  onOpenPopUp: () => void;
  onClosePopUp: () => void;
  discrepency?: boolean;
  loading: boolean;
  onSubmitResponse: (
    payload: AddSurveyResponsePayload | AddSurveyResponsePayloadSidebar,
    shouldGoToNextCase?: boolean,
  ) => Promise<void>;
}

interface DataAnnotationProviderProps {
  children: React.ReactNode;
  discrepency?: boolean;
  inPatientDetails?: boolean;
}

const DataAnnotationContext = React.createContext<DataAnnotationContextProps>({
  popUpOpen: false,
  currentCase: undefined,
  onOpenPopUp: () => {},
  onClosePopUp: () => {},
  discrepency: false,
  loading: false,
  onSubmitResponse: async () => Promise.resolve(),
});
export const useDataAnnotation = () => React.useContext(DataAnnotationContext);

const DataAnnotationProvider = ({
  children,
  discrepency,
  inPatientDetails = false,
}: DataAnnotationProviderProps) => {
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const { resolvingSubmitCount } = useSelector(
    (state: RootState) => state.dataAnnotation,
  );
  const dispatch = useDispatch();
  const [popUpOpen, setPopUpOpen] = React.useState<boolean>(false);
  const [currentCase, setCurrentCase] = React.useState<CaseTO>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const caseType = discrepency ? CaseType.DISCREPANCY : CaseType.NORMAL;
  const questionType = discrepency
    ? QuestionType.DISCREPENCY
    : QuestionType.NORMAL;

  const initialiseQuestions = () => {
    const questionnaires = selectedTenancy?.questionnaires || [];
    const currentQuestionnaire =
      questionnaires.find((q) => q.title === questionType) || null;
    if (currentQuestionnaire) {
      dispatch(setCurrentQuestionnaire({ currentQuestionnaire }));
    }
  };

  const onSubmitResponse = async (
    payload: AddSurveyResponsePayload | AddSurveyResponsePayloadSidebar,
    shouldGoToNextCase = false,
  ) => {
    try {
      const shouldGetNextCase =
        !discrepency ||
        (discrepency && resolvingSubmitCount > 0) ||
        shouldGoToNextCase;
      setLoading(true);
      const requestBody:
        | AddSurveyResponsePayload
        | AddSurveyResponsePayloadSidebar = {
        ...payload,
      };
      const {
        data: { message },
      } = await post('/addSurveyResponse', {
        ...requestBody,
      });
      if (
        message === 'Success' &&
        shouldGetNextCase &&
        !requestBody.access_token
      ) {
        await loadAnnotationData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onOpenPopUp = () => {
    setPopUpOpen(true);
  };
  const onClosePopUp = () => {
    setPopUpOpen(false);
  };

  const loadAnnotationData = async () => {
    if (!selectedTenancy) return;
    const requestBody: GetAnnotationCasePayload = {
      tenancyId: selectedTenancy.tenancy_id,
      caseType,
    };

    try {
      setLoading(true);
      const {
        data: { data },
      } = await post('/getAnnotationCase', requestBody);
      if (!data) {
        setCurrentCase(undefined);
        return;
      }
      const caseTO = toCaseTO(data);
      setCurrentCase(caseTO);
      dispatch(
        setDataAnnotationData({
          dataAnnotationPatientProfile: caseTO.patientHeader,
          additionalSection: caseTO.additionalSection,
          observations: caseTO.observationTable,
          surveys: caseTO.surveys || [],
        }),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initialiseQuestions();
    if (!inPatientDetails) {
      loadAnnotationData();
    }
  }, [inPatientDetails, discrepency]);

  return (
    <DataAnnotationContext.Provider
      value={{
        loading,
        currentCase,
        discrepency,
        popUpOpen,
        onOpenPopUp,
        onClosePopUp,
        onSubmitResponse,
      }}
    >
      {children}
    </DataAnnotationContext.Provider>
  );
};

export default DataAnnotationProvider;
